import { useEffect, useState } from "react";
import { CheckCircle2, Pencil, X } from "lucide-react";
import { Field, TextInput, TextArea } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const AdminTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ subject: "", body: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = () => api.get("/admin/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    try {
      await api.put(`/admin/templates/${edit.key}`, { key: edit.key, ...f });
      setEdit(null);
      setNotice("Template saved — the next enquiry email uses it.");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-templates">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Email templates</h1>
        <p className="text-sm text-brand-mist">
          Transactional emails sent by the site. Templates support tokens like {"{{name}}"}, {"{{enquiry_id}}"} and {"{{service}}"}.
        </p>
      </div>
      {notice && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="templates-saved"><CheckCircle2 size={15} /> {notice}</p>}
      <div className="surface-card divide-y divide-brand-mist/10" data-testid="templates-list">
        {templates.length === 0 && <p className="px-6 py-5 text-sm text-brand-mist">No editable templates yet — code defaults are in use.</p>}
        {templates.map((t) => (
          <div key={t.key} className="flex items-center justify-between px-6 py-4" data-testid={`template-row-${t.key}`}>
            <div>
              <p className="text-sm font-medium text-brand-snow">{t.key}</p>
              <p className="text-xs text-brand-mist">{t.subject}</p>
            </div>
            <button onClick={() => { setEdit(t); setF({ subject: t.subject, body: t.body }); }} data-testid={`template-edit-${t.key}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-snow hover:border-brand-emerald hover:text-brand-emerald">
              <Pencil size={12} /> Edit
            </button>
          </div>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={() => setEdit(null)} data-testid="template-editor-modal">
          <form onSubmit={save} className="h-full w-full max-w-2xl space-y-5 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-brand-snow">Edit template: {edit.key}</h2>
              <button type="button" onClick={() => setEdit(null)} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <Field label="Subject" htmlFor="tpl-subject" required><TextInput id="tpl-subject" testid="tpl-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} required /></Field>
            <Field label="Body" htmlFor="tpl-body" required hint="Plain text; line breaks are preserved. Brand shell is added automatically.">
              <TextArea id="tpl-body" rows={10} testid="tpl-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} required />
            </Field>
            {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
            <button type="submit" data-testid="tpl-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Save template</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminTemplates;
